import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Share2, Copy, MessageCircle, ExternalLink } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import Footer from '../components/Footer';
import { CertificateCard } from './CertificatePreview';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CertificatePage() {
  const { certId } = useParams();
  const [cert, setCert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/certificat/${certId}`)
      .then(res => { if (!res.ok) throw new Error(); return res.json(); })
      .then(data => { setCert(data); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, [certId]);

  if (loading) return (
    <div className="min-h-screen bg-[#0B1521] flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-[#4A90D9] animate-spin" />
    </div>
  );

  if (error || !cert) return (
    <div className="min-h-screen bg-[#0B1521]">
      <Navbar />
      <div className="flex flex-col items-center justify-center pt-32 px-4">
        <XCircle className="w-16 h-16 text-red-400 mb-4" />
        <h1 className="text-2xl font-bold text-white mb-2">Certificat non trouve</h1>
        <p className="text-white/50 text-sm">L'identifiant {certId} ne correspond a aucun certificat.</p>
      </div>
      <Footer />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0B1521]">
      <Navbar />
      <div className="pt-24 pb-16 px-4">
        <div className="max-w-[900px] mx-auto">
          {/* Verified badge */}
          <div className="flex items-center justify-center gap-2 mb-6">
            <CheckCircle className="w-5 h-5 text-emerald-400" />
            <p className="text-emerald-400 font-semibold text-sm">Certificat verifie et authentique</p>
          </div>

          {/* Certificate */}
          <CertificateCard cert={cert} />

          {/* Share Section */}
          <ShareSection cert={cert} />
        </div>
      </div>
      <Footer />
    </div>
  );
}

function ShareSection({ cert }) {
  const [copied, setCopied] = useState(false);
  const certUrl = `${window.location.origin}/certificat/${cert.certificate_id}`;
  const shareText = `Je viens d'obtenir mon certificat JoSmartTech "${cert.formation_title}" ! Rejoignez-moi et beneficiez de 10% de reduction : ${certUrl}`;

  const copyLink = () => {
    navigator.clipboard.writeText(certUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="mt-6 bg-gradient-to-r from-[#4A90D9]/10 to-[#8B5CF6]/10 border border-white/10 rounded-xl p-6" data-testid="share-referral-section">
      <div className="flex items-center gap-2 mb-3">
        <Share2 size={20} className="text-[#4A90D9]" />
        <h3 className="text-lg font-bold text-white">Partagez votre reussite</h3>
      </div>
      <p className="text-white/50 text-sm mb-4">
        Partagez votre certificat et offrez 10% de reduction a vos amis sur les formations JoSmartTech !
      </p>
      <div className="flex flex-wrap gap-2 mb-4">
        <button onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, '_blank')}
          className="flex items-center gap-2 px-4 py-2.5 bg-[#25D366]/20 text-[#25D366] rounded-lg text-sm font-semibold hover:bg-[#25D366]/30 transition-colors" data-testid="share-whatsapp">
          <MessageCircle size={16} />WhatsApp
        </button>
        <button onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(certUrl)}`, '_blank')}
          className="flex items-center gap-2 px-4 py-2.5 bg-[#1877F2]/20 text-[#1877F2] rounded-lg text-sm font-semibold hover:bg-[#1877F2]/30 transition-colors" data-testid="share-facebook">
          <ExternalLink size={16} />Facebook
        </button>
        <button onClick={() => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(certUrl)}`, '_blank')}
          className="flex items-center gap-2 px-4 py-2.5 bg-[#0A66C2]/20 text-[#0A66C2] rounded-lg text-sm font-semibold hover:bg-[#0A66C2]/30 transition-colors" data-testid="share-linkedin">
          <ExternalLink size={16} />LinkedIn
        </button>
        <button onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`, '_blank')}
          className="flex items-center gap-2 px-4 py-2.5 bg-white/10 text-white/70 rounded-lg text-sm font-semibold hover:bg-white/20 transition-colors" data-testid="share-twitter">
          <ExternalLink size={16} />X
        </button>
      </div>
      <div className="flex items-center gap-2 bg-white/5 rounded-lg p-3">
        <input type="text" readOnly value={certUrl} className="flex-1 bg-transparent text-white/60 text-xs outline-none font-mono" />
        <button onClick={copyLink} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${copied ? 'bg-green-500/20 text-green-400' : 'bg-[#4A90D9]/20 text-[#4A90D9] hover:bg-[#4A90D9]/30'}`} data-testid="copy-cert-link">
          <Copy size={14} />{copied ? 'Copie !' : 'Copier'}
        </button>
      </div>
    </div>
  );
}
